import React, { useState } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Bell, 
  BellOff, 
  Shield, 
  Smartphone, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Settings,
  TestTube,
  Info
} from 'lucide-react';

const NotificationSettings = ({ onClose }) => {
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    loading, 
    subscribe, 
    unsubscribe, 
    testNotification 
  } = useNotifications();
  
  const { language, isRTL } = useLanguage();
  const [showDetails, setShowDetails] = useState(false);

  const getPermissionStatus = () => {
    switch (permission) {
      case 'granted':
        return {
          icon: CheckCircle,
          text: language === 'en' ? 'Granted' : 'הוענק',
          color: '#10b981',
          bgColor: '#dcfce7'
        };
      case 'denied':
        return {
          icon: XCircle,
          text: language === 'en' ? 'Denied' : 'נדחה',
          color: '#ef4444',
          bgColor: '#fef2f2'
        };
      default:
        return {
          icon: AlertTriangle,
          text: language === 'en' ? 'Not Set' : 'לא נקבע',
          color: '#f59e0b',
          bgColor: '#fef3c7'
        };
    }
  };

  const getSubscriptionStatus = () => {
    if (isSubscribed) {
      return {
        icon: Bell,
        text: language === 'en' ? 'Active' : 'פעיל',
        color: '#10b981',
        bgColor: '#dcfce7'
      };
    } else {
      return {
        icon: BellOff,
        text: language === 'en' ? 'Inactive' : 'לא פעיל',
        color: '#6b7280',
        bgColor: '#f3f4f6'
      };
    }
  };

  const handleSubscribe = async () => {
    const success = await subscribe();
    if (success) {
      // Optional: Show additional success message or guidance
    }
  };

  const handleUnsubscribe = async () => {
    const confirmed = window.confirm(
      language === 'en' 
        ? 'Are you sure you want to unsubscribe from notifications?' 
        : 'האם אתה בטוח שברצונך לבטל את המנוי להתראות?'
    );
    
    if (confirmed) {
      await unsubscribe();
    }
  };

  const handleTestNotification = async () => {
    await testNotification();
  };

  if (!isSupported) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20
      }}>
        <div style={{
          background: 'white',
          borderRadius: 16,
          padding: 32,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
          direction: isRTL ? 'rtl' : 'ltr'
        }}>
          <XCircle style={{ width: 48, height: 48, color: '#ef4444', marginBottom: 16 }} />
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#18181b', marginBottom: 12 }}>
            {language === 'en' ? 'Not Supported' : 'לא נתמך'}
          </h3>
          <p style={{ color: '#6b7280', marginBottom: 24, lineHeight: 1.5 }}>
            {language === 'en' 
              ? 'Push notifications are not supported in this browser. Please try using a modern browser like Chrome, Firefox, or Safari.'
              : 'התראות דחיפה אינן נתמכות בדפדפן זה. אנא נסה להשתמש בדפדפן מודרני כמו Chrome, Firefox או Safari.'
            }
          </p>
          <button
            onClick={onClose}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {language === 'en' ? 'Close' : 'סגור'}
          </button>
        </div>
      </div>
    );
  }

  const permissionStatus = getPermissionStatus();
  const subscriptionStatus = getSubscriptionStatus();
  const PermissionIcon = permissionStatus.icon;
  const SubscriptionIcon = subscriptionStatus.icon;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20
    }}>
      <div style={{
        background: 'white',
        borderRadius: 16,
        padding: 32,
        maxWidth: 500,
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Settings style={{ width: 24, height: 24, color: '#dc2626' }} />
            <h3 style={{ fontSize: 20, fontWeight: 700, color: '#18181b', margin: 0 }}>
              {language === 'en' ? 'Notification Settings' : 'הגדרות התראות'}
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#f3f4f6',
              color: '#6b7280',
              border: 'none',
              borderRadius: 8,
              padding: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <XCircle style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {/* Status Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
          {/* Permission Status */}
          <div style={{
            background: permissionStatus.bgColor,
            border: `1px solid ${permissionStatus.color}20`,
            borderRadius: 12,
            padding: 16,
            textAlign: 'center'
          }}>
            <PermissionIcon style={{ width: 32, height: 32, color: permissionStatus.color, marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#18181b', marginBottom: 4 }}>
              {language === 'en' ? 'Permission' : 'הרשאה'}
            </div>
            <div style={{ fontSize: 12, color: permissionStatus.color, fontWeight: 600 }}>
              {permissionStatus.text}
            </div>
          </div>

          {/* Subscription Status */}
          <div style={{
            background: subscriptionStatus.bgColor,
            border: `1px solid ${subscriptionStatus.color}20`,
            borderRadius: 12,
            padding: 16,
            textAlign: 'center'
          }}>
            <SubscriptionIcon style={{ width: 32, height: 32, color: subscriptionStatus.color, marginBottom: 8 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: '#18181b', marginBottom: 4 }}>
              {language === 'en' ? 'Subscription' : 'מנוי'}
            </div>
            <div style={{ fontSize: 12, color: subscriptionStatus.color, fontWeight: 600 }}>
              {subscriptionStatus.text}
            </div>
          </div>
        </div>

        {/* Description */}
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fee2e2',
          borderRadius: 12,
          padding: 16,
          marginBottom: 24
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <Info style={{ width: 20, height: 20, color: '#0284c7', marginTop: 2, flexShrink: 0 }} />
            <div style={{ color: '#0c4a6e', fontSize: 14, lineHeight: 1.5 }}>
              {language === 'en' 
                ? 'Enable notifications to receive updates about games, bet results, and important announcements directly on your device.'
                : 'הפעל התראות כדי לקבל עדכונים על משחקים, תוצאות הימורים והודעות חשובות ישירות למכשיר שלך.'
              }
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!isSubscribed ? (
            <button
              onClick={handleSubscribe}
              disabled={loading}
              style={{
                background: loading ? '#d1d5db' : 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                padding: '16px 24px',
                fontSize: 16,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                transition: 'all 0.2s'
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: 20,
                    height: 20,
                    border: '2px solid #ffffff40',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  {language === 'en' ? 'Subscribing...' : 'נרשם...'}
                </>
              ) : (
                <>
                  <Bell style={{ width: 20, height: 20 }} />
                  {language === 'en' ? 'Enable Notifications' : 'הפעל התראות'}
                </>
              )}
            </button>
          ) : (
            <>
              {/* Test Notification Button */}
              <button
                onClick={handleTestNotification}
                disabled={loading}
                style={{
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: 12,
                  padding: '16px 24px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all 0.2s'
                }}
              >
                <TestTube style={{ width: 20, height: 20 }} />
                {language === 'en' ? 'Send Test Notification' : 'שלח התראת בדיקה'}
              </button>

              {/* Unsubscribe Button */}
              <button
                onClick={handleUnsubscribe}
                disabled={loading}
                style={{
                  background: loading ? '#f3f4f6' : '#fef2f2',
                  color: loading ? '#9ca3af' : '#dc2626',
                  border: `1px solid ${loading ? '#e5e7eb' : '#fecaca'}`,
                  borderRadius: 12,
                  padding: '16px 24px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all 0.2s'
                }}
              >
                <BellOff style={{ width: 20, height: 20 }} />
                {language === 'en' ? 'Disable Notifications' : 'בטל התראות'}
              </button>
            </>
          )}
        </div>

        {/* Technical Details Toggle */}
        <div style={{ marginTop: 24, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
          <button
            onClick={() => setShowDetails(!showDetails)}
            style={{
              background: 'none',
              border: 'none',
              color: '#dc2626',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              justifyContent: 'center'
            }}
          >
            <Smartphone style={{ width: 16, height: 16 }} />
            {language === 'en' ? 'Technical Details' : 'פרטים טכניים'}
          </button>

          {showDetails && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
              <div style={{ marginBottom: 8 }}>
                <strong>{language === 'en' ? 'Browser:' : 'דפדפן:'}</strong> {navigator.userAgent.split(' ').slice(-2).join(' ')}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>{language === 'en' ? 'Platform:' : 'פלטפורמה:'}</strong> {navigator.platform}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>{language === 'en' ? 'Language:' : 'שפה:'}</strong> {navigator.language}
              </div>
              <div>
                <strong>{language === 'en' ? 'Support:' : 'תמיכה:'}</strong> {isSupported ? '✅' : '❌'}
              </div>
            </div>
          )}
        </div>

        {/* Browser Instructions */}
        {permission === 'denied' && (
          <div style={{
            marginTop: 16,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 12,
            padding: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <Shield style={{ width: 20, height: 20, color: '#dc2626', marginTop: 2, flexShrink: 0 }} />
              <div style={{ color: '#991b1b', fontSize: 14, lineHeight: 1.5 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  {language === 'en' ? 'Notifications Blocked' : 'התראות חסומות'}
                </div>
                {language === 'en' 
                  ? 'To enable notifications, click the lock icon in your browser\'s address bar and allow notifications for this site.'
                  : 'כדי להפעיל התראות, לחץ על סמל המנעול בשורת הכתובת של הדפדפן ואפשר התראות לאתר זה.'
                }
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add CSS animation for loading spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default NotificationSettings;
